import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { ACCOUNT_API_GET_ACCOUNT_LIST } from 'apiRoutes';
import { useGetOnMount } from 'hooks/useGetOnMount';
import { accountTableColumns } from 'components/account/accountTableColumns';
import { getProductUserColumns } from './productUserColumns';
import { Table } from 'components/tables/Table';
import { useNavigate } from 'react-router';
import { ProductName } from 'domain/product/enums/ProductName';
import useAuthRoles from 'hooks/useAuthRoles';
import { columnFilters } from './columnFilters';
import ProductContext from '../ProductContext';
import { toDefaultDateFormat } from 'utils/dateUtils';
import moment from 'moment';
import { ExcelExportType } from 'components/tables/Table.interfaces';
import { VStack } from '@montel/montelpro-shared-components-v2';
import { MontelAccount } from 'domain/account/interfaces';
import { getProductConfigByName } from 'domain/product/productMap';
import { ReviewAccountModal } from 'components/account/Review/Modal/ReviewAccountModal';
import AssignToCompanyFormContextProvider from 'components/companies/AssignToCompanyForm/AssignToCompanyFormContext';

interface ProductUsersProps {
	productName: ProductName;
}

const ProductUsers: FunctionComponent<ProductUsersProps> = ({
	productName,
}) => {
	const productConfig = getProductConfigByName(productName);
	const { data: usersData, status: usersStatus } = useGetOnMount(
		`${ACCOUNT_API_GET_ACCOUNT_LIST}?productId=${productConfig?.id}`,
	);

	const { canChangeProductAccess } = useAuthRoles();

	const [users, setUsers] = useState<MontelAccount[]>([]);
	const [showReviewModal, setShowReviewModal] = useState<boolean>(false);
	const [activeAccount, setActiveAccount] = useState<MontelAccount>();

	useEffect(() => {
		if (!usersData) return;

		const filteredUsers = usersData.filter(
			(user: MontelAccount) => !user?.locked,
		);

		setUsers(filteredUsers);
	}, [usersData]);

	const forceRerenderUsers = () => {
		const refreshedUsers = users.map((x) => x);
		setUsers(refreshedUsers);
	};

	const onClickReview = (account: MontelAccount) => {
		setActiveAccount(account);
		setShowReviewModal(true);
	};

	const onUserReviewed = (accountId: string, productId: string) => {
		const updatedUsers = users.map((user) => {
			if (user.id === accountId) {
				return {
					...user,
					productRegistrations: user.productRegistrations.map(
						(registration) => {
							if (registration.productId === productId) {
								return {
									...registration,
									reviewed: true,
								};
							}
							return registration;
						},
					),
				};
			}
			return user;
		});

		setUsers(updatedUsers);
	};

	const productUserColumns = getProductUserColumns(productConfig?.id);

	const columns = useMemo(() => {
		const columns = [
			accountTableColumns.fullName(),
			accountTableColumns.email(),
			accountTableColumns.phoneNumber(),
			accountTableColumns.registeredCompany({
				headCellProps: {
					text: 'Company',
				},
			}),
			productUserColumns.dateRegistered(),
			productUserColumns.lastLogin(),
			productUserColumns.lastActive(),
			productUserColumns.accessStatus(),
			productUserColumns.markReviewed(forceRerenderUsers, onClickReview),
		];

		return columns;
	}, [canChangeProductAccess]);

	return (
		<ProductContext productName={productName}>
			<VStack gap="lg" fullWidth>
				<Table<MontelAccount>
					tableId={`${productName.toLowerCase()}_users_table`}
					columns={columns}
					data={users}
					withPagination
					withSearch
					showRowCount
					columnVisibility={{
						markReviewed: canChangeProductAccess(
							getProductConfigByName(productName)?.id ?? '',
						),
					}}
					filterSettings={columnFilters(productName)}
					initialSorting={[
						{
							id: 'dateRegistered',
							desc: true,
						},
					]}
					linkTo={(row: MontelAccount) => `/accounts/users/${row.id}`}
					exportOptions={{
						documentName: `${productName} ${toDefaultDateFormat(
							moment().utc(),
						)}`,
						buttonTitle: `Export ${productName} users`,
						type: ExcelExportType.ProductUser,
					}}
					requestStatus={usersStatus}
				/>
			</VStack>
			{activeAccount && (
				<AssignToCompanyFormContextProvider account={activeAccount}>
					<ReviewAccountModal
						showModal={showReviewModal}
						setShowModal={setShowReviewModal}
						onSuccess={onUserReviewed}
						account={activeAccount}
						productId={productConfig?.id}
						productName={productConfig?.name}
					/>
				</AssignToCompanyFormContextProvider>
			)}
		</ProductContext>
	);
};

export default ProductUsers;
